import React, { useEffect, useMemo, memo } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import MainCard from 'components/MainCard';
import { Typography, Box, Stack, CircularProgress, useMediaQuery } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { motion } from 'framer-motion';

// Import SBS components
import SideBySideReport from 'sections/sbs/SideBySideReport';
// Import actions
import { setSecondaryMenuVisible } from 'store/reducers/menu';
import { setFaviconWithColor } from 'store/reducers/faviconCacheReducer';

/**
 * Access control adapter to handle feature access based on analysisResults.access
 */
const useAccessControl = (analysisResults) => {
  return useMemo(() => {
    if (!analysisResults?.access) {
      return {
        canAccessAnalysisResults: true,
        canAccessFieldByField: true,
        canAccessSideBySide: true
      };
    }

    const { features } = analysisResults.access;

    return {
      canAccessAnalysisResults: features?.analysis_results?.enabled ?? true,
      canAccessFieldByField: features?.field_by_field_analysis?.enabled ?? true,
      canAccessSideBySide: features?.side_by_side_analysis?.enabled ?? true
    };
  }, [analysisResults?.access]);
};

/**
 * New version of the CompetitorsAnalysisResult component
 * that handles the updated data structure with is_new_analysis flag
 * and reuses components from the Side-by-Side (SBS) module
 */
const NewCompetitorsAnalysisResult = ({ showSecondaryMenu = true }) => {
  const theme = useTheme();
  const matchDownSM = useMediaQuery(theme.breakpoints.down('sm'));
  const dispatch = useDispatch();

  // Memoize the menu visibility state
  const menuVisibility = useMemo(() => showSecondaryMenu, [showSecondaryMenu]);

  // Get data from Redux store
  const analysisResults = useSelector((state) => state.competitorsReducer.competitorsAnalysisResults);
  const isLoadingResults = useSelector((state) => state.competitorsReducer.competitorsAnalysisResultStatus === 'processing');

  // Get access control status
  const accessControl = useAccessControl(analysisResults);

  // Set the secondary menu visibility and dispatch favicons when the component mounts
  useEffect(() => {
    dispatch(setSecondaryMenuVisible({ visible: menuVisibility }));

    if (analysisResults?.analysis_results?.results) {
      analysisResults.analysis_results.results.forEach((company) => {
        if (company.url && company.favicon) {
          dispatch(setFaviconWithColor(company.url, company.favicon));
        }
      });
    }

    return () => {
      if (menuVisibility) {
        dispatch(setSecondaryMenuVisible({ visible: false }));
      }
    };
  }, [dispatch, analysisResults, menuVisibility]);

  // If we don't have results yet or they're still loading (be tolerant for free plans)
  if (!analysisResults || isLoadingResults || !analysisResults?.analysis_results) {
    return (
      <MainCard>
        <Stack direction="row" spacing={2} alignItems="center" justifyContent="center" sx={{ py: 4 }}>
          <CircularProgress />
          <Typography variant="h5">Loading analysis results...</Typography>
        </Stack>
      </MainCard>
    );
  }

  // Respect top-level access control: require analysis_results access only (let child sections gate field-by-field)
  if (!accessControl.canAccessAnalysisResults) {
    return null;
  }

  return (
    <MainCard style={{ margin: matchDownSM ? -20 : 0 }} border={false}>
      <Box sx={{ maxWidth: '100%' }}>
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
          <Box sx={{ mt: 3 }}>
            <SideBySideReport
              analysisData={analysisResults?.analysis_results || []}
              companyData={{
                ...analysisResults?.company_data,
                field_by_field_analysis: analysisResults?.field_by_field_analysis,
                overview_result: analysisResults?.company_data?.overview_result || [],
                usp: analysisResults?.company_data?.usp || {},
                market_trends: analysisResults?.company_data?.market_trends || {},
                pricing_analysis: analysisResults?.company_data?.pricing_analysis || {},
                access: analysisResults?.access
              }}
            />
          </Box>
        </motion.div>
      </Box>
    </MainCard>
  );
};

export default memo(NewCompetitorsAnalysisResult);
